import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { trackPageView } from './lib/analytics';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import UnifiedWorkflow from './pages/UnifiedWorkflow';
import MyProjects from './pages/MyProjects';
import ImageAnalysis from './pages/ImageAnalysis';
import Account from './pages/Account';
import AdminPanel from './pages/AdminPanel';
import AIProviderManagement from './pages/AIProviderManagement';
import UserManagement from './pages/UserManagement';
import PlatformROI from './pages/PlatformROI';
import FunnelDashboard from './pages/FunnelDashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PreviewPage from './pages/PreviewPage';
import AuthCallback from './pages/AuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Legacy pages (accessible via direct URL only)
import STLAnalysis from './pages/STLAnalysis';
import STLRepair from './pages/STLRepair';
import SmartSupport from './pages/SmartSupport';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

// Track page views on route change
const PageViewTracker = ({ children }) => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return children;
};

// Tiny fixed badge in the bottom-right that ONLY shows when the
// internal-user flag is active. Lets the operator confirm at a glance
// that their own activity is being excluded from analytics.
const InternalUserBadge = () => {
  const isInternal = typeof window !== 'undefined' && window.__pmIsInternalUser;
  if (!isInternal) return null;
  return (
    <div
      data-testid="internal-user-badge"
      style={{
        position: 'fixed',
        bottom: 8,
        right: 8,
        zIndex: 9999,
        padding: '4px 10px',
        borderRadius: 999,
        background: 'rgba(255, 200, 0, 0.18)',
        border: '1px solid rgba(255, 200, 0, 0.45)',
        color: '#ffd54a',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        backdropFilter: 'blur(6px)',
        pointerEvents: 'none',
      }}
      title="Your activity is excluded from analytics. Run localStorage.removeItem('internal_user') to disable."
    >
      🛡 Internal user — analytics filtered
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/preview" element={<PreviewPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />

        {/* Core Tools */}
        <Route path="smart-support" element={<UnifiedWorkflow />} />
        <Route path="generate-supports" element={<SmartSupport />} />
        <Route path="repair-analysis" element={<UnifiedWorkflow mode="repair-only" />} />
        <Route path="diagnosis" element={<ImageAnalysis />} />

        {/* Unified Projects */}
        <Route path="projects" element={<MyProjects />} />

        {/* Settings */}
        <Route path="settings" element={<Account />} />

        {/* Admin */}
        <Route path="admin" element={<AdminPanel />} />
        <Route path="admin/ai-providers" element={<AIProviderManagement />} />
        <Route path="admin/users" element={<UserManagement />} />
        <Route path="admin/platform-roi" element={<PlatformROI />} />
        <Route path="admin/funnel" element={<FunnelDashboard />} />

        {/* Legacy routes — accessible via direct URL only */}
        <Route path="stl-analysis" element={<STLAnalysis />} />
        <Route path="stl-repair" element={<STLRepair />} />
        <Route path="old-smart-support" element={<SmartSupport />} />
        <Route path="support-history" element={<Navigate to="/dashboard/projects" replace />} />
        <Route path="account" element={<Navigate to="/dashboard/settings" replace />} />
        <Route path="image-analysis" element={<Navigate to="/dashboard/diagnosis" replace />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PageViewTracker>
            <AppRoutes />
            <InternalUserBadge />
          </PageViewTracker>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
